// Freight Cost (bảng giá mua của hãng) + lưới Hãng × Tuyến của bàn Pricing.
// Ngưỡng và hiệu lực dùng `@hana/domain` (rateValidity, PRICING_THRESHOLDS); giá mua nằm ở `CostBuy`, tách khỏi `CostRate`.
import { findPort, rateValidity, type RegionKey } from '@hana/domain';
import type { CostRate } from './types';

export type CellStatus = 'ok' | 'soon' | 'exp' | 'none';
export type RegionGroup = 'ASIA' | 'EUROPE' | 'AMERICA' | 'OTHER';

export const REGION_GROUPS: readonly { key: RegionGroup; label: string }[] = [
  { key: 'ASIA', label: 'Châu Á' },
  { key: 'EUROPE', label: 'Châu Âu' },
  { key: 'AMERICA', label: 'Châu Mỹ' },
  { key: 'OTHER', label: 'Khác' },
];

const GROUP_OF: Partial<Record<RegionKey, RegionGroup>> = {
  ASIA_EASTERN: 'ASIA',
  ASIA_SOUTH_EASTERN: 'ASIA',
  ASIA_SOUTHERN: 'ASIA',
  ASIA_WESTERN: 'ASIA',
  MIDDLE_EAST: 'ASIA',
  EUROPE_WESTERN: 'EUROPE',
  EUROPE_EASTERN: 'EUROPE',
  MEDITERRANEAN: 'EUROPE',
  NORTHERN_AMERICA: 'AMERICA',
  AMERICA_CENTRAL: 'AMERICA',
  LATIN_AMERICA: 'AMERICA',
  CARIBBEAN: 'AMERICA',
};

export const groupOfPod = (pod: string): RegionGroup => {
  const region = findPort(pod)?.region;
  return (region && GROUP_OF[region]) || 'OTHER';
};

export const routeKey = (r: { pol: string; pod: string }): string => `${r.pol}→${r.pod}`;
export const routeLabel = (r: { pol: string; pod: string }): string => `${r.pol} → ${r.pod}`;

export interface Cell {
  carrier: string;
  status: CellStatus;
  /** ngày còn lại tới ValidEnd (âm = đã hết), của dòng giá tốt nhất của ô */
  daysLeft: number | null;
  /** hướng so kỳ trước: ▲ tăng · ▼ giảm · 0 giữ (chỉ khi có quyền tiền ở màn) */
  trend: 1 | -1 | 0 | null;
}
export interface GridRow {
  route: string;
  group: RegionGroup;
  cells: (Cell | null)[];
  /** số hãng có giá còn hạn / số hãng chạy tuyến */
  served: number;
  covered: number;
}
export interface Grid {
  carriers: string[];
  rows: GridRow[];
}

/** Dòng hiệu lực của một ô: ưu tiên còn hạn (sắp hết đứng sau còn dài), không có thì dòng hết hạn gần nhất. */
function statusOf(rates: readonly CostRate[], today: string): Pick<Cell, 'status' | 'daysLeft'> & { rate: CostRate | null } {
  let best: { rate: CostRate; daysLeft: number; status: CellStatus } | null = null;
  for (const r of rates) {
    const v = rateValidity({ validFrom: r.validFrom, validEnd: r.validEnd }, today);
    if (v.status === 'upcoming') continue;
    const status: CellStatus = v.status === 'expired' ? 'exp' : v.status === 'expiring' ? 'soon' : 'ok';
    const rank = (s: CellStatus) => (s === 'exp' ? 0 : s === 'soon' ? 1 : 2);
    if (!best || rank(status) > rank(best.status) || (status === best.status && v.daysLeft > best.daysLeft)) best = { rate: r, daysLeft: v.daysLeft, status };
  }
  return best ? { status: best.status, daysLeft: best.daysLeft, rate: best.rate } : { status: 'none', daysLeft: null, rate: null };
}

/**
 * Lưới Hãng × Tuyến. `serve` = hãng nào chạy tuyến nào (ô "hãng không chạy tuyến" = null);
 * ô không có dòng giá nhưng hãng chạy tuyến = "Chưa có giá".
 */
export function buildGrid(rates: readonly CostRate[], serve: ReadonlyMap<string, ReadonlySet<string>>, carriers: readonly string[], today: string, trendOf?: (r: CostRate) => Cell['trend']): Grid {
  const byCell = new Map<string, CostRate[]>();
  for (const r of rates) {
    const k = `${routeKey(r)}|${r.carrier}`;
    (byCell.get(k) ?? byCell.set(k, []).get(k)!).push(r);
  }
  const rows: GridRow[] = [];
  for (const [rk, set] of serve) {
    const [pol = '', pod = ''] = rk.split('→');
    const cells = carriers.map((c): Cell | null => {
      if (!set.has(c)) return null;
      const st = statusOf(byCell.get(`${rk}|${c}`) ?? [], today);
      return { carrier: c, status: st.status, daysLeft: st.daysLeft, trend: st.rate && trendOf ? trendOf(st.rate) : null };
    });
    const served = cells.filter(Boolean).length;
    const covered = cells.filter((c) => c && (c.status === 'ok' || c.status === 'soon')).length;
    rows.push({ route: routeLabel({ pol, pod }), group: groupOfPod(pod), cells, served, covered });
  }
  return { carriers: [...carriers], rows };
}

export function countStatus(grid: Grid, s: CellStatus): number {
  let n = 0;
  for (const r of grid.rows) for (const c of r.cells) if (c?.status === s) n++;
  return n;
}

export interface CostFilter {
  q: string;
  group: RegionGroup | null;
  carrier: string | null;
  /** ẩn giá hết hạn (mặc định của board) */
  hideExpired: boolean;
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();

export function filterCost(rates: readonly CostRate[], f: CostFilter, today: string): CostRate[] {
  const q = fold(f.q.trim());
  return rates.filter((r) => {
    if (f.carrier && r.carrier !== f.carrier) return false;
    if (f.group && groupOfPod(r.pod) !== f.group) return false;
    if (f.hideExpired && rateValidity({ validFrom: r.validFrom, validEnd: r.validEnd }, today).status === 'expired') return false;
    if (q && !fold(`${r.pol} ${r.pod} ${r.carrier} ${r.source} ${r.via ?? ''}`).includes(q)) return false;
    return true;
  });
}

export type ContKey = 'dc20' | 'dc40' | 'hc40' | 'rf40';
export const CONT_KEYS: readonly { key: ContKey; label: string }[] = [
  { key: 'dc20', label: '20DC' },
  { key: 'dc40', label: '40DC' },
  { key: 'hc40', label: '40HC' },
  { key: 'rf40', label: '40RF' },
];

export interface LookupRow {
  carrier: string;
  rate: CostRate;
  daysLeft: number;
  expiring: boolean;
  /** giá mua của loại cont đang chọn; `null` khi không có quyền tiền */
  buy: number | null;
  /** ▲ ▼ so kỳ trước (chỉ 40HC có kỳ trước) */
  trend: 1 | -1 | 0 | null;
}
export interface Lookup {
  rows: LookupRow[];
  /** hãng chạy tuyến nhưng chưa có giá còn hạn → nút "Hỏi hãng" */
  none: string[];
}

/** Tra cứu nhanh (điện thoại): một tuyến, mỗi hãng một dòng giá còn hạn tốt nhất; có giá mua thì xếp rẻ → đắt, không thì theo hãng. */
export function lookupCost(
  rates: readonly CostRate[],
  serve: ReadonlyMap<string, ReadonlySet<string>>,
  q: { pol: string; pod: string; cont: ContKey; today: string },
  buys: ReadonlyMap<string, { dc20: number; dc40: number; hc40: number; rf40: number; prevHc40: number }> | null,
): Lookup {
  const carriers = [...(serve.get(routeKey(q)) ?? [])];
  const rows: LookupRow[] = [];
  const none: string[] = [];
  for (const c of carriers) {
    const mine = rates.filter((r) => r.pol === q.pol && r.pod === q.pod && r.carrier === c);
    const st = statusOf(mine, q.today);
    if (!st.rate || st.status === 'exp') {
      none.push(c);
      continue;
    }
    const b = buys?.get(st.rate.id);
    rows.push({
      carrier: c,
      rate: st.rate,
      daysLeft: st.daysLeft ?? 0,
      expiring: st.status === 'soon',
      buy: b ? b[q.cont] : null,
      trend: b && q.cont === 'hc40' ? (Math.sign(b.hc40 - b.prevHc40) as 1 | -1 | 0) : null,
    });
  }
  rows.sort((a, b) => (a.buy ?? 0) - (b.buy ?? 0) || a.carrier.localeCompare(b.carrier));
  return { rows, none };
}
